import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, Grid, LayoutGrid, Sparkles, Check } from 'lucide-react';
import Header from './Header';
import SEO from './SEO';

const ArchiveIndexPage = () => {
  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="Game Archives - PocketHints" 
        description="Browse the complete archives for Pocket Grids and Syllo puzzles. Find past answers and hints."
        url={window.location.origin + '/archives'}
      />

      <Header date={new Date().toISOString()} title="Game Archives" gameName="Archives" />

      <main className="flex-grow max-w-6xl mx-auto px-4 w-full pb-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-6">
            Select an Archive
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choose a game to browse its complete history of puzzles, hints, and answers.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Pocket Grids Archive Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="group bg-white rounded-3xl p-8 border-2 border-transparent hover:border-brand-blue/10 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center"
          >
            <div className="bg-brand-blue/10 p-6 rounded-2xl group-hover:bg-brand-blue/20 transition-colors mb-6">
               <img 
                  src={process.env.PUBLIC_URL + '/pocket-grids-logo.png'}
                  alt="Pocket Grids Logo" 
                  className="w-20 h-20 object-contain"
                />
            </div>
            
            <h3 className="text-2xl font-black text-gray-900 mb-3 group-hover:text-brand-blue transition-colors">
              Pocket Grids Archive
            </h3>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Browse all past daily crossword grids. Filter by date and find the help you need.
            </p>

            <Link 
              to="/archive"
              className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-blue text-white text-lg font-bold rounded-xl shadow-lg hover:bg-blue-600 hover:scale-105 transition-all duration-200"
            >
              <Calendar className="w-5 h-5" />
              View Archive
            </Link>
            
            <Link 
               to="/todays-pocket-grids-clues-answers"
               className="mt-4 text-sm font-bold text-gray-400 hover:text-brand-blue transition-colors flex items-center gap-1"
            >
               Go to Today's Puzzle <ArrowRight className="w-3 h-3" />
            </Link>
          </motion.div>

          {/* Syllo Archive Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="group bg-white rounded-3xl p-8 border-2 border-transparent hover:border-brand-blue/10 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center"
          >
            <div className="bg-brand-blue/10 p-6 rounded-2xl group-hover:bg-brand-blue/20 transition-colors mb-6">
               <img 
                  src={process.env.PUBLIC_URL + '/Syllo-logo.png'}
                  alt="Syllo Logo" 
                  className="w-20 h-20 object-contain"
                />
            </div>
            
            <h3 className="text-2xl font-black text-gray-900 mb-3 group-hover:text-brand-blue transition-colors">
              Syllo Archive
            </h3>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Explore the history of syllable merging puzzles. Find past challenges and solutions.
            </p>

            <Link 
              to="/syllo-archive"
              className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-blue text-white text-lg font-bold rounded-xl shadow-lg hover:bg-blue-600 hover:scale-105 transition-all duration-200"
            >
              <Calendar className="w-5 h-5" />
              View Archive
            </Link>

            <Link 
               to="/todays-syllo-clues-answers"
               className="mt-4 text-sm font-bold text-gray-400 hover:text-brand-blue transition-colors flex items-center gap-1"
            >
               Go to Today's Puzzle <ArrowRight className="w-3 h-3" />
            </Link>
          </motion.div>

          {/* Hot and Cold Archive Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="group bg-white rounded-3xl p-8 border-2 border-transparent hover:border-brand-blue/10 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center"
          >
            <div className="bg-brand-blue/10 p-6 rounded-2xl group-hover:bg-brand-blue/20 transition-colors mb-6">
               <img 
                  src={process.env.PUBLIC_URL + '/hotandcold-logo.png'}
                  alt="Hot and Cold Logo" 
                  className="w-20 h-20 object-contain"
                />
            </div>
            
            <h3 className="text-2xl font-black text-gray-900 mb-3 group-hover:text-brand-blue transition-colors">
              Hot and Cold Archive
            </h3>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Browse past Hot and Cold puzzles. Find hints and answers for previous games.
            </p>

            <Link 
              to="/hot-and-cold-archive"
              className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-blue text-white text-lg font-bold rounded-xl shadow-lg hover:bg-blue-600 hover:scale-105 transition-all duration-200"
            >
              <Calendar className="w-5 h-5" />
              View Archive
            </Link>

            <Link 
               to="/todays-hot-and-cold-clues-answers"
               className="mt-4 text-sm font-bold text-gray-400 hover:text-brand-blue transition-colors flex items-center gap-1"
            >
               Go to Today's Puzzle <ArrowRight className="w-3 h-3" />
            </Link>
          </motion.div>

          {/* Byrdle Archive Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="group bg-white rounded-3xl p-8 border-2 border-transparent hover:border-brand-blue/10 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center"
          >
            <div className="bg-brand-blue/10 p-6 rounded-2xl group-hover:bg-brand-blue/20 transition-colors mb-6">
               <img 
                  src={process.env.PUBLIC_URL + '/byrdle-logo.png'}
                  alt="Byrdle Logo" 
                  className="w-20 h-20 object-contain"
                />
            </div>
            
            <h3 className="text-2xl font-black text-gray-900 mb-3 group-hover:text-brand-blue transition-colors">
              Byrdle Archive
            </h3>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Browse past Byrdle choral puzzles. Find hints and answers for previous games.
            </p>

            <Link 
              to="/byrdle-archive"
              className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-blue text-white text-lg font-bold rounded-xl shadow-lg hover:bg-blue-600 hover:scale-105 transition-all duration-200"
            >
              <Calendar className="w-5 h-5" />
              View Archive
            </Link>

            <Link 
               to="/todays-byrdle-clues-answers"
               className="mt-4 text-sm font-bold text-gray-400 hover:text-brand-blue transition-colors flex items-center gap-1"
            >
               Go to Today's Puzzle <ArrowRight className="w-3 h-3" />
            </Link>
          </motion.div>

          {/* DailyGuess Solver Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="group bg-white rounded-3xl p-8 border-2 border-transparent hover:border-green-500/10 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center md:col-span-2 lg:col-span-1"
          >
            <div className="bg-green-100 p-6 rounded-2xl group-hover:bg-green-200 transition-colors mb-6">
               <Check className="w-20 h-20 text-green-600" />
            </div>
            
            <h3 className="text-2xl font-black text-gray-900 mb-3 group-hover:text-green-600 transition-colors">
              DailyGuess Solver
            </h3>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Instant helper for Wordle and DailyGuess. Enter your tiles to find the answer.
            </p>

            <Link 
              to="/daily-guess-solver"
              className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-green-600 text-white text-lg font-bold rounded-xl shadow-lg hover:bg-green-700 hover:scale-105 transition-all duration-200"
            >
              <Check className="w-5 h-5" />
              Open Solver
            </Link>
          </motion.div>

        </div>
      </main>
    </div>
  );
};

export default ArchiveIndexPage;

